/**
 * E2E key persistence -- IndexedDB for private keys, Supabase for public keys and wrapped chat keys.
 * Mirrors E2EKeyRepository.swift on iOS/macOS.
 */

import { supabase } from '../../../utils/supabase/client';
import * as E2E from './e2eEncryption';

const DB_NAME = 'quantis-e2e';
const DB_VERSION = 1;
const STORE_KEYS = 'private-keys';

// Session-scoped cache: avoids repeated Supabase fetches for the same chat key.
const chatKeyCache = new Map<string, CryptoKey>();

export type ChatEncryptionRequirement = 'required' | 'legacy' | 'unknown';

// ── IndexedDB helpers ────────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_KEYS)) {
        req.result.createObjectStore(STORE_KEYS);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_KEYS, 'readonly').objectStore(STORE_KEYS).get(key);
    req.onsuccess = () => resolve((req.result as T) ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key: string, value: unknown): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_KEYS, 'readwrite').objectStore(STORE_KEYS).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ── Private key management ───────────────────────────────────────────────────

/** Read-only recovery accessor: never creates or publishes an identity. */
export async function getExistingPrivateKey(userId: string): Promise<CryptoKey | null> {
  const stored = await idbGet<JsonWebKey>(userId);
  return stored ? E2E.importPrivateKeyFromJwk(stored) : null;
}

/** Loads the user's X25519 private key from IndexedDB, generating and persisting a new one if absent. */
export async function getOrCreatePrivateKey(userId: string): Promise<CryptoKey> {
  const stored = await idbGet<JsonWebKey>(userId);
  if (stored) {
    return E2E.importPrivateKeyFromJwk(stored);
  }
  const { privateKey } = await E2E.generateKeyPair();
  const jwk = await E2E.exportPrivateKeyJwk(privateKey);
  await idbPut(userId, jwk);
  return privateKey;
}

/**
 * Ensures the user has a key pair and uploads the public key to Supabase.
 * Called on every sign-in -- idempotent (upsert).
 */
export async function publishPublicKey(userId: string): Promise<void> {
  try {
    const privateKey = await getOrCreatePrivateKey(userId);
    const publicKeyBase64 = await E2E.publicKeyBase64FromPrivateKey(privateKey);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('user_encryption_keys')
      .upsert({ user_id: userId, public_key: publicKeyBase64, key_version: 1 });
    if (error) throw error;
  } catch (err) {
    console.warn('[E2E] Failed to publish public key:', err);
  }
}

// ── Chat key management ──────────────────────────────────────────────────────

/** Fetches and unwraps this user's chat key. Returns null if not yet provisioned. */
export async function getChatKey(chatId: string, userId: string): Promise<CryptoKey | null> {
  const cacheKey = `${chatId}:${userId}`;
  const cached = chatKeyCache.get(cacheKey);
  if (cached) return cached;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from('chat_encryption_keys')
      .select('wrapped_key')
      .eq('chat_id', chatId)
      .eq('user_id', userId)
      .maybeSingle() as { data: { wrapped_key: string } | null; error: unknown };

    if (error || !data?.wrapped_key) return null;

    const privateKey = await getOrCreatePrivateKey(userId);
    const chatKey = await E2E.unwrapChatKey(data.wrapped_key, privateKey, chatId);
    chatKeyCache.set(cacheKey, chatKey);
    return chatKey;
  } catch {
    return null;
  }
}

/**
 * Distinguishes intentionally plaintext legacy chats from encrypted chats
 * whose local/wrapped key is temporarily unavailable. Unknown is fail-closed:
 * a database error must not silently turn an encrypted send into plaintext.
 */
export async function getChatEncryptionRequirement(
  chatId: string,
  userId: string,
): Promise<ChatEncryptionRequirement> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: wrappedRow, error: wrappedError } = await (supabase as any)
      .from('chat_encryption_keys')
      .select('wrapped_key')
      .eq('chat_id', chatId)
      .eq('user_id', userId)
      .maybeSingle() as { data: { wrapped_key?: string } | null; error: unknown };
    if (wrappedError) return 'unknown';
    if (wrappedRow) return 'required';

    // A participant added after key provisioning may have no wrapped row of
    // their own, but an existing encrypted message still makes the policy clear.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: encryptedRows, error: messageError } = await (supabase as any)
      .from('messages')
      .select('id')
      .eq('chat_id', chatId)
      .eq('encrypted', true)
      .limit(1) as { data: Array<{ id: string }> | null; error: unknown };
    if (messageError) return 'unknown';
    return encryptedRows && encryptedRows.length > 0 ? 'required' : 'legacy';
  } catch {
    return 'unknown';
  }
}

/**
 * Provisions a random AES-256 chat key for all participants who have published a public key.
 * Idempotent -- short-circuits if this user's wrapped key already exists.
 */
export async function provisionChatKeyIfNeeded(
  chatId: string,
  userId: string,
  participantIds: string[],
): Promise<void> {
  if (await getChatKey(chatId, userId)) return;

  try {
    const chatKey = await E2E.generateChatKey();
    const allParticipants = Array.from(new Set([...participantIds, userId]));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: keyRows } = await (supabase as any)
      .from('user_encryption_keys')
      .select('user_id, public_key')
      .in('user_id', allParticipants) as { data: Array<{ user_id: string; public_key: string }> | null };

    if (!keyRows || keyRows.length === 0) return;

    const rows = await Promise.all(
      keyRows.map(async (row) => ({
        chat_id: chatId,
        user_id: row.user_id,
        wrapped_key: await E2E.wrapChatKey(chatKey, row.public_key, chatId),
      })),
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from('chat_encryption_keys').upsert(rows);

    chatKeyCache.set(`${chatId}:${userId}`, chatKey);
  } catch (err) {
    console.warn('[E2E] Failed to provision chat key:', err);
  }
}
